/**
 * useAttentionSuccess (KAN-761, KAN-903)
 *
 * "Успех" of a client (attention block on the dashboard and in CRM, CRM table
 * and board card, manager app /m) opens the order compose of the client at once:
 * - POST /api/v1/orders/ {client_id} is idempotent: the client's current cart
 *   (draft) or a new one; its compose dialog opens;
 * - "Оформить заказ" (place) in the compose: the order is placed, then the
 *   outcome success is set;
 * - "Пропустить" (success without an order): the outcome only;
 * - closing the compose (cross, Escape): nothing is sent, no outcome.
 * The cart stays as it is in both cases, even empty (owner decision).
 * While POST is in flight the client is pending (isPending(clientId)): the
 * row buttons are disabled; a ref also drops a second click in the same tick.
 * Without orders for the user (useCanCallApi POST /orders/ false) or for the
 * organization (POST answers 404: not an e-commerce one) the plain dialog
 * "Отметить успех" opens instead; another POST error: a toast and the same
 * plain dialog, so success can still be marked.
 * The role is not checked here: only api_access (useCanCallApi) and 404 decide.
 */
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCanCallApi } from '@/hooks/useApiAccess'
import { isNotFoundError } from '@/hooks/useCatalog'
import { useCreateOrder } from '@/hooks/useOrders'
import { extractErrorMessage } from '@/lib/extractErrorMessage'
import type { AttentionItem } from '@/services/api/dashboard'
import type { RunAttentionAction } from './useAttentionDone'

interface ComposeState {
  item: AttentionItem
  orderId: string
}

export function useAttentionSuccess(run: RunAttentionAction) {
  const canOrders = useCanCallApi('POST', '/api/v1/orders/')
  const create = useCreateOrder()
  // Client whose cart POST is in flight (ref: two clicks in one tick see it).
  const creatingRef = useRef<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  // Plain "Отметить успех" dialog (no orders); null: closed.
  const [dialogItem, setDialogItem] = useState<AttentionItem | null>(null)
  // Order compose opened by "Успех"; null: closed.
  const [compose, setCompose] = useState<ComposeState | null>(null)

  /** Set the outcome of the open client and close both dialogs. */
  const markSuccess = (item: AttentionItem | null | undefined) => {
    setDialogItem(null)
    setCompose(null)
    if (item) run(item.client_id, 'success')
  }

  const setCreatingFor = (clientId: string | null) => {
    creatingRef.current = clientId
    setCreating(clientId)
  }

  return {
    /** POST /orders/ is in flight. */
    pending: creating !== null,
    isPending: (clientId: string) => creating === clientId,
    request: (item: AttentionItem) => {
      if (!canOrders) {
        setDialogItem(item)
        return
      }
      if (creatingRef.current !== null) return
      setCreatingFor(item.client_id)
      create.mutate(item.client_id, {
        onSuccess: (order) => setCompose({ item, orderId: order.id }),
        onError: (error) => {
          if (!isNotFoundError(error)) toast.error(extractErrorMessage(error, 'Не удалось открыть корзину клиента'))
          setDialogItem(item)
        },
        onSettled: () => setCreatingFor(null),
      })
    },
    dialogItem,
    cancel: () => setDialogItem(null),
    markOnly: () => markSuccess(dialogItem),
    composeItem: compose?.item ?? null,
    composeOrderId: compose?.orderId ?? null,
    /** Cross / Escape of the compose: no outcome. */
    closeCompose: () => setCompose(null),
    /** "Пропустить". */
    skipOrder: () => markSuccess(compose?.item),
    /** The order is placed from the compose. */
    placed: () => markSuccess(compose?.item),
  }
}

export type AttentionSuccess = ReturnType<typeof useAttentionSuccess>
